/**
 * URL generators for external travel sites.
 * All use search URL templates — no API keys needed.
 */

// ── Destination-level link generators ──

export const getGoogleMapsUrl = (city: string, country: string): string =>
  `https://www.google.com/maps/search/${encodeURIComponent(`${city}, ${country}`)}`;

export const getTripAdvisorUrl = (city: string, country: string): string =>
  `https://www.tripadvisor.com/Search?q=${encodeURIComponent(`${city} ${country}`)}`;

export const getBookingUrl = (city: string, country: string): string =>
  `https://www.booking.com/searchresults.html?ss=${encodeURIComponent(`${city}, ${country}`)}`;

export const getGoogleFlightsUrl = (city: string, country: string): string =>
  `https://www.google.com/travel/flights?q=flights+to+${encodeURIComponent(`${city} ${country}`)}`;

export const getViatorUrl = (city: string, country: string): string =>
  `https://www.viator.com/searchResults/all?text=${encodeURIComponent(`${city} ${country}`)}`;

export const getGetYourGuideUrl = (city: string, country: string): string =>
  `https://www.getyourguide.com/s/?q=${encodeURIComponent(`${city}, ${country}`)}`;

// ── Per-POI link ──

export const getPoiGoogleMapsUrl = (name: string, lat?: number, lon?: number): string => {
  if (lat !== undefined && lon !== undefined && lat !== 0 && lon !== 0) {
    return `https://www.google.com/maps/search/${encodeURIComponent(name)}/@${lat},${lon},17z`;
  }
  return `https://www.google.com/maps/search/${encodeURIComponent(name)}`;
};

// ── Per-category TripAdvisor drill-down ──

const CATEGORY_SEARCH_TERMS: Record<string, string> = {
  attractions: 'things to do',
  restaurants: 'restaurants',
  museums: 'museums',
  parks: 'parks nature',
  shopping: 'shopping',
  nightlife: 'nightlife',
};

export const getCategoryTripAdvisorUrl = (city: string, country: string, category: string): string => {
  const term = CATEGORY_SEARCH_TERMS[category] || category;
  return `https://www.tripadvisor.com/Search?q=${encodeURIComponent(`${city} ${country} ${term}`)}`;
};

// ── External links configuration for rendering ──

export interface ExternalLinkItem {
  id: string;
  labelKey: string;        // i18n key under destinations:externalLinks
  icon: string;            // heroicon identifier
  color: string;           // Tailwind text color
  getUrl: (city: string, country: string) => string;
  group: string;           // grouping key
}

export const EXTERNAL_LINKS: ExternalLinkItem[] = [
  {
    id: 'google_maps',
    labelKey: 'googleMaps',
    icon: 'MapPinIcon',
    color: 'text-green-600 dark:text-green-400',
    getUrl: getGoogleMapsUrl,
    group: 'general',
  },
  {
    id: 'tripadvisor',
    labelKey: 'tripadvisor',
    icon: 'GlobeAltIcon',
    color: 'text-green-600 dark:text-green-400',
    getUrl: getTripAdvisorUrl,
    group: 'general',
  },
  {
    id: 'booking',
    labelKey: 'booking',
    icon: 'BuildingOfficeIcon',
    color: 'text-blue-700 dark:text-blue-400',
    getUrl: getBookingUrl,
    group: 'accommodation',
  },
  {
    id: 'google_flights',
    labelKey: 'googleFlights',
    icon: 'PaperAirplaneIcon',
    color: 'text-blue-500 dark:text-blue-400',
    getUrl: getGoogleFlightsUrl,
    group: 'gettingThere',
  },
  {
    id: 'viator',
    labelKey: 'viator',
    icon: 'TicketIcon',
    color: 'text-purple-600 dark:text-purple-400',
    getUrl: getViatorUrl,
    group: 'toursActivities',
  },
  {
    id: 'getyourguide',
    labelKey: 'getYourGuide',
    icon: 'MapIcon',
    color: 'text-orange-500 dark:text-orange-400',
    getUrl: getGetYourGuideUrl,
    group: 'toursActivities',
  },
];

export const LINK_GROUPS = [
  { key: 'general', labelKey: 'general' },
  { key: 'accommodation', labelKey: 'accommodation' },
  { key: 'gettingThere', labelKey: 'gettingThere' },
  { key: 'toursActivities', labelKey: 'toursActivities' },
  { key: 'safety', labelKey: 'safety' },
];
